// REST client for the sensor catalog + per-company mapping (issue #4).
//
// All endpoints are company-scoped; `company` threads the admin's selected
// tenant through as ?company=<slug> (admins have no implicit tenant — same
// contract as the alert-rule client). Non-admins pass null and are pinned to
// their own slug server-side.
//
// The api speaks the data-plane machine identity: `machine` is the external
// machines.machine_id, never the UUID. Localized labels are display-only — the
// raw field_name is what the alert engine matches.

import { API_BASE } from '@/apiConfig';
import { withCompany } from '@/lib/withCompany';

/** One catalogued (machine, field) pair the platform has actually seen. */
export interface CatalogEntry {
  machine_id:          string; // machines.id (UUID) — opaque to the client
  machine_external_id: string; // machines.machine_id (data-plane id)
  field_name:          string;
}

/** One display-name override row. `machine` null = company-global tier. */
export interface SensorMapping {
  company_slug: string;
  machine:      string | null; // external machine id, or null (global)
  field_name:   string;
  display_label: string;
}

async function parseError(res: Response): Promise<string> {
  try {
    const j = await res.json() as { error?: string };
    if (j?.error) return j.error;
  } catch { /* non-JSON */ }
  return `HTTP ${res.status}`;
}

export async function listCatalog(
  token: string, company: string | null, machineExternalId?: string,
): Promise<CatalogEntry[]> {
  let url = withCompany(`${API_BASE}/sensors/catalog`, company);
  if (machineExternalId) {
    url += `${url.includes('?') ? '&' : '?'}machine=${encodeURIComponent(machineExternalId)}`;
  }
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json() as CatalogEntry[]) ?? [];
}

export async function listMappings(
  token: string, company: string | null,
): Promise<SensorMapping[]> {
  const res = await fetch(withCompany(`${API_BASE}/sensors/mappings`, company), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json() as SensorMapping[]) ?? [];
}

/** Upsert one display name. A blank label reverts the tier (server deletes the
 *  row). machine omitted/undefined = the company-global tier. */
export async function upsertMapping(
  token: string,
  input: { field: string; label: string; machine?: string | null },
  company: string | null,
): Promise<void> {
  const res = await fetch(withCompany(`${API_BASE}/sensors/mappings`, company), {
    method:  'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      field:   input.field,
      label:   input.label,
      machine: input.machine ?? '',
    }),
  });
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}

export async function deleteMapping(
  token: string,
  input: { field: string; machine?: string | null },
  company: string | null,
): Promise<void> {
  let url = withCompany(`${API_BASE}/sensors/mappings`, company);
  url += `${url.includes('?') ? '&' : '?'}field=${encodeURIComponent(input.field)}`;
  if (input.machine) url += `&machine=${encodeURIComponent(input.machine)}`;
  const res = await fetch(url, {
    method:  'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}
